<template>
  <div class="form--feedback">
    <div class="form__header text-center">
      <h2 class="no-margin">
        Feedback<span v-show="showThankYou"> Sent</span>
      </h2>
    </div>

    <form class="form__content-wrapper">
      <div class="form__content">
        <div v-show="showThankYou">
          <h3 class="flex flex-v-center">
            <i class="icon--tick margin-space--right" />
            <span>Thank you for your feedback</span>
          </h3>

          <button
            class="button--accent"
            @click.prevent="resetForm"
          >
            Close
          </button>
        </div>

        <div v-show="!showThankYou">
          <div class="form__field">
            <label for="radios-location">Is your feedback related to a specific location or for the whole layer?</label>
            <radio-buttons
              id="radios-location"
              name="includeLocation"
              :options="locationToggleOptions"
              @update:selected-option="toggleIncludeLocation"
            />

            <div
              v-show="includeLocation"
              class="form__location flex flex-v-center flex-h-between gutters"
            >
              <p class="flex flex-v-center">
                <label
                  for="location"
                  class="no-margin margin-space--right"
                >Location: </label>
                <span>{{ location }}</span>
              </p>
              <v-select
                id="location"
                input-id="location"
                :options="locationOptions"
                :selected="defaultLocationType"
                @update:selected-option="toggleLocation"
              />
            </div>
          </div>

          <div class="form__field">
            <label for="radios-positive">According to your expertise, does the layer accurately reflect the reality?</label>
            <radio-buttons
              id="radios-positive"
              name="positive"
              :options="positiveOptions"
              @update:selected-option="togglePositive"
            />
          </div>

          <div
            v-if="!inputPositive"
            class="form__field"
          >
            <label for="comment">Please explain why</label>
            <textarea
              id="comment"
              v-model="inputComment"
              required
            />
          </div>

          <div class="form__field">
            <label for="reflect_habitat_occurence">Does this dataset accurately reflect the occurrence of this habitat in your area of interest? If not, please explain.</label>
            <textarea
              id="reflect_habitat_occurence"
              v-model="inputReflectHabitatOccurence"
            />
          </div>

          <div class="form__field">
            <label for="better_spatial_data">Are you aware of better spatial data for this location? If so, please provide further information on the source. In particular, please identify the governmental agency that would be responsible for this type of information in the area of interest.</label>
            <textarea
              id="better_spatial_data"
              v-model="inputBetterSpatialData"
            />
          </div>

          <div class="form__field">
            <label for="files">If you have better data that you can supply us to help improve the dataset, please attach or direct us to the relevant data and associated metadata. We will then contact you to discuss in further detail.</label>

            <div class="js-uppy-input" />
            <div class="js-uppy-status-bar" />
            <div class="js-uppy-progress-bar" />
            <ul
              :id="uploadedFilesId"
              class="ul--unstyled"
            >
              <li
                v-for="(file, index) in uploadedFiles"
                :key="`form-feedback-${_uid}-file-${index}`"
                class="form__file flex flex-v-center flex-h-between"
              >
                <span>
                  <strong>File:</strong> {{ getFileNameString(file) }}
                </span>
                <button
                  class="button--remove-upload"
                  @click.prevent="removeFile(file)"
                />
              </li>
            </ul>
          </div>

          <div class="form__field">
            <label for="url">Option to supply URL link</label>
            <input
              id="url"
              v-model="inputUrl"
              type="url"
            >
          </div>

          <div class="text-center">
            <button
              class="button--underline margin-space--right"
              @click.prevent="resetForm"
            >
              Cancel
            </button>

            <button
              class="button--accent margin-space--left"
              type="submit"
              @click="submit"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
const Uppy = require('@uppy/core')
const ProgressBar = require('@uppy/progress-bar')
const FileInput = require('@uppy/file-input')
const StatusBar = require('@uppy/status-bar')
const AwsS3 = require('@uppy/aws-s3')

require('@uppy/core/dist/style.css')
require('@uppy/file-input/dist/style.css')
require('@uppy/progress-bar/dist/style.css')
require('@uppy/status-bar/dist/style.css')
require('whatwg-fetch')

import './form.scss'

import RadioButtons from '../radio/RadioButtons'
import VSelect from '../select/VSelect'
import { submitFeedback } from '../../helpers/request-helpers'

export default {
  name: 'FormMap',

  components: { RadioButtons, VSelect },

  props: {
    id: {
      type: String,
      required: true
    },
    modalId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      location: '',
      inputUrl: '',
      inputComment: '',
      inputBetterSpatialData: '',
      inputReflectHabitatOccurence: '',
      inputPositive: true,
      includeLocation: true,
      locationToggleOptions: [
        {
          id: 'include-location-yes',
          title: 'Location specific'
        },
        {
          id: 'include-location-no',
          title: 'Whole layer'
        }
      ],
      locationOptions: [
        {
          id: 'coord',
          name: 'Latitude and longtitude'
        },
        {
          id: 'country',
          name: 'Country'
        },
        {
          id: 'region',
          name: 'Continent'
        }
      ],
      positiveOptions: [
        {
          id: 'positive-yes',
          title: 'Yes'
        },
        {
          id: 'positive-no',
          title: 'No'
        }
      ],
      showThankYou: false,
      uploadedFilesId: 'uploaded-files',
      getUppy: () => {},
      uploadedFiles: [],
      defaultLocationType: null,
      locationType: ''
    }
  },

  computed: {
    latLng () {
      const lat = this.$store.state.form.lat,
        lng = this.$store.state.form.lng

      return lat && lng ? `${lat.toFixed(3)}, ${lng.toFixed(3)}` : ''
    },

    selectedLayerId () {
      return this.$store.state.map.selectedLayerId
    },

    uploadedFileData () {
      if (this.uploadedFiles[0]) {
        return {
          upload_file: this.uploadedFiles[0].data.name,
          upload_url: this.uploadedFiles[0].meta.key
        }
      }

      return {}
    }
  },

  mounted () {
    this.$eventHub.$on(`modal-open-${this.modalId}`, this.initForm)
    this.$eventHub.$on(`close-${this.id}`, this.resetForm)

    const uppy = new Uppy({
      autoProceed: true,
      allowMultipleUploads: true,
      restrictions: {
        maxFileSize: 1000 * 1024 ** 2,
        maxNumberOfFiles: 1,
      }
    })

    uppy
      .use(FileInput, { target: '.js-uppy-input' })
      .use(StatusBar, { target: '.js-uppy-status-bar' })
      .use(ProgressBar, { target: '.js-uppy-progress-bar' })
      .use(AwsS3, {
        companionUrl: '/'
      })
      .on('upload-success', this.onUploadSuccess)

    this.getUppy = () => uppy
  },

  methods: {
    getFileNameString (file) {
      const units = ['B', 'KB', 'MB']
      let fileSize = file.size
      let unitPrefixIndex = 0

      while (fileSize > 1000) {
        fileSize /= 1024
        unitPrefixIndex++
      }

      return `${file.name}, ${fileSize.toPrecision(3)}${units[unitPrefixIndex]}`
    },

    submit (e) {
      if(this.$el.querySelector('form').checkValidity()){
        e.preventDefault()
      }

      const data = {
        feedback: {
          lat: this.$store.state.form.lat,
          long: this.$store.state.form.lng,
          comments: this.inputComment,
          reflect_habitat_occurence: this.inputReflectHabitatOccurence,
          better_spatial_data: this.inputBetterSpatialData,
          positive: this.inputPositive,
          url: this.inputUrl,
          layer_version_id: this.selectedLayerId,
          display: this.locationType,
          ... this.uploadedFileData
        }
      }

      submitFeedback(data, () => {
        this.showThankYou = true
      })
    },

    cancel () {
      this.$eventHub.$emit(`modal-close-${this.modalId}`)
    },

    onUploadSuccess () {
      this.updateUploadedFiles()
    },

    updateUploadedFiles () {
      this.uploadedFiles = this.getUppy() ? this.getUppy().getFiles() : []
    },

    removeFile (file) {
      this.getUppy().removeFile(file.id)
      this.updateUploadedFiles()
    },

    toggleIncludeLocation (selected) {
      if(selected == 'include-location-yes') {
        this.includeLocation = true
        this.defaultLocationType = {...this.defaultLocationType}
      } else {
        this.includeLocation = false
        this.toggleLocation('global')
      }
    },

    toggleLocation (selected) {
      this.locationType = selected

      if(selected == 'coord') {
        this.location = this.latLng

      } else if (selected == 'country') {
        this.location = this.$store.state.form.country

      } else if (selected == 'region') {
        this.location = this.$store.state.form.region

      } else {
        this.location = ''
      }
    },

    togglePositive (selected) {
      this.inputPositive = selected === 'positive-yes' ? true : false
    },

    initForm () {
      this.defaultLocationType = {...this.locationOptions[0]}
    },

    resetForm () {
      this.$store.dispatch('form/resetForm')
      this.$eventHub.$emit(`modal-close-${this.modalId}`)
      this.$eventHub.$emit('resetForm')
      this.inputComment = ''
      this.inputReflectHabitatOccurence = ''
      this.inputBetterSpatialData = ''
      this.inputUrl = ''
      this.location = ''
      this.showThankYou = false
      this.getUppy().cancelAll()
      this.updateUploadedFiles()
    }
  }
}
</script>
